import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { Coach } from '@backrs_ui/common-ui/src/models/coach';

import { Avatar, Box, Button, SxProps, Theme, Typography } from '@mui/material';

const CONSULT_COACH_CARD_MAX_WIDTH = 286;
const CONSULT_COACH_CARD_MAX_HEIGHT = 367;
const PROFILE_IMAGE_SIZE_MAX = 82;
const MAX_VISIBLE_BADGES = 2;

const STRINGS = {
  coachLabel: appStrings.modules.nugget.coach,
  ctaLabel: appStrings.modules.deepDive.card.ctaLabel,
  moreBadges: (count: number) => `${count} more`,
};

type ConsultCoachCardProps = Readonly<{
  coach: Coach;
  variant?: 'normal' | 'simple';
  onCardClick?: (coach: Coach) => void;
  onCtaClick?: (coach: Coach) => void;
}>;

export const ConsultCoachCard = ({
  coach,
  variant = 'normal',
  onCardClick,
  onCtaClick,
}: ConsultCoachCardProps) => {
  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCtaClick?.(coach);
  };

  const fullName = `${coach.first_name} ${coach.last_name}`;
  const hasCalendarLink = !!coach.consultation_calendar_url;
  const deepDiveNames = coach.deep_dives?.map((dd) => dd.name) ?? [];
  const visibleBadges = deepDiveNames.slice(0, MAX_VISIBLE_BADGES);
  const remainingBadgeCount = deepDiveNames.length - MAX_VISIBLE_BADGES;

  return (
    <Box sx={cardSx(variant)} onClick={() => onCardClick?.(coach)}>
      <Box sx={contentSx}>
        {/* Header: Profile image + Name */}
        <Box sx={headerSx}>
          <Box sx={profileImageWrapSx}>
            {coach.profile_image_url ? (
              <Box component="img" src={coach.profile_image_url} sx={profileImageSx} />
            ) : (
              <Box sx={profileImagePlaceholderSx} />
            )}
          </Box>
          <Box sx={nameContainerSx}>
            <Typography variant="text-lg-semibold" sx={nameSx}>
              {fullName}
            </Typography>
            {coach.headline && (
              <Typography variant="text-sm-regular" sx={headlineSx}>
                {coach.headline}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Info lines */}
        <Box sx={infoLinesSx}>
          {coach.job_title && (
            <Box sx={infoLineSx}>
              <AppIcon iconName="Clock" diameter="16px" color="Gray/400" />
              <Typography variant="text-sm-regular" sx={infoTextSx}>
                {coach.job_title}
              </Typography>
            </Box>
          )}
          {coach.education?.[0] && (
            <Box sx={infoLineSx}>
              {coach.education[0].logo_url ? (
                <Avatar src={coach.education[0].logo_url} sx={infoLogoSx} />
              ) : (
                <AppIcon iconName="Bank" diameter="16px" color="Gray/400" />
              )}
              <Typography variant="text-sm-regular" sx={infoTextSx}>
                {coach.education[0].name}
              </Typography>
            </Box>
          )}
          {coach.employers?.[0] && (
            <Box sx={infoLineSx}>
              {coach.employers[0].logo_url ? (
                <Avatar src={coach.employers[0].logo_url} sx={infoLogoSx} />
              ) : (
                <AppIcon iconName="Building06" diameter="16px" color="Gray/400" />
              )}
              <Typography variant="text-sm-regular" sx={infoTextSx}>
                {coach.employers[0].name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bottom section: Category badges + CTA grouped together */}
        <Box sx={bottomSectionSx}>
          {deepDiveNames.length > 0 && (
            <Box sx={badgesContainerSx}>
              {visibleBadges.map((badge) => (
                <Box key={badge} sx={categoryBadgeSx}>
                  <Typography variant="text-xs-medium">{badge}</Typography>
                </Box>
              ))}
              {remainingBadgeCount > 0 && (
                <Box sx={moreBadgeSx}>
                  <AppIcon iconName="Plus" diameter="12px" color="Brand/500" />
                  <Typography variant="text-xs-medium">
                    {STRINGS.moreBadges(remainingBadgeCount)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {variant !== 'simple' && (
            <Button
              id="consult-coach-schedule-btn"
              hierarchy="Primary"
              size="lg"
              fullWidth
              disabled={!hasCalendarLink}
              onClick={handleCtaClick}
            >
              <AppIcon iconName="CalendarPlus01" diameter="20px" color="currentColor" />
              <Typography variant="text-sm-semibold">{STRINGS.ctaLabel}</Typography>
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const cardSx =
  (variant: 'normal' | 'simple') =>
  ({ backrs }: Theme) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    borderRadius: backrs.radius['radius-4xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    overflow: 'hidden',
    width: variant === 'normal' ? '100%' : 'unset',
    maxWidth: variant === 'normal' ? CONSULT_COACH_CARD_MAX_WIDTH : 'unset',
    minWidth: variant === 'normal' ? '268px' : 'unset',
    height: CONSULT_COACH_CARD_MAX_HEIGHT,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    cursor: 'pointer',
    userSelect: 'none' as const,
    transition: 'all 0.3s ease-in-out',
    boxShadow: `0px 0px 0px 0px ${backrs.colors['Colors/Base/Black']}`,

    '&:hover': {
      borderColor: backrs.colors['Colors/Foreground/fg-primary'],
      transform: 'translate(-2px, -2px)',
      boxShadow: backrs.shadows['shadow-lg'],
    },

    '&:active': {
      transform: 'none !important',
      boxShadow: 'none !important',
      transition: 'all 0s !important',
    },
  });

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  paddingX: backrs.spacing['spacing-xl'],
  paddingTop: backrs.spacing['spacing-xl'],
  paddingBottom: backrs.spacing['spacing-3xl'],
  width: '100%',
  flex: 1,
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-xl'],
  alignItems: 'flex-start',
});

const profileImageWrapSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  width: PROFILE_IMAGE_SIZE_MAX,
  height: PROFILE_IMAGE_SIZE_MAX,
  minWidth: PROFILE_IMAGE_SIZE_MAX,
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  overflow: 'hidden',
});

const profileImageSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const profileImagePlaceholderSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: backrs.colors['Gray/200'],
});

const nameContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  flex: 1,
  minWidth: 0,
});

const nameSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  wordBreak: 'break-word',
});

const headlineSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const infoLinesSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const infoLineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const infoLogoSx: SxProps<Theme> = ({ backrs }) => ({
  width: '16px',
  height: '16px',
  borderRadius: backrs.radius['radius-md'],
});

const infoTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const badgesContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
});

const categoryBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  color: backrs.colors['Brand/700'],
  borderRadius: '16px',
  paddingX: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-xxs'],
});

const moreBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  color: backrs.colors['Brand/700'],
  borderRadius: '16px',
  paddingLeft: backrs.spacing['spacing-sm'],
  paddingRight: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-xxs'],
});

const bottomSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  marginTop: 'auto',
});
